import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPosOrder, getPosCustomerByTelegram } from "@/api/pos-api";
import { useCollectionData } from "@/hooks/use-api";
import { toast } from "@/hooks/use-toast";
import type { OrderResponse } from "@/types/order-type";
import { usePosCart, POS_CART_STORAGE_KEY } from "../hooks/use-pos-cart";
import type { TProductWithRelation } from "@/types/product-type";
import { getStorage, setStorage } from "@/hooks/use-local-storage";
import { useLocation } from "wouter";
import { routes } from "@/routes/route-constants";
import type { Customer } from "@/types/customer-type";

const POS_ORDER_HISTORY_KEY = "pos-order-history";
const POS_CUSTOMER_INFO_KEY = "pos-customer-info";
const MAX_ORDER_HISTORY = 50;

export { POS_CUSTOMER_INFO_KEY };

export type PosOrderHistoryItem = {
  id: string;
  orderNumber: string;
  totalAmount: number;
  date: string;
};

export type PosScreen = "menu" | "cart" | "payment" | "confirmation";
export type PosDisplayMode = "products" | "variants";

const POS_DISPLAY_MODE_KEY = "pos-display-mode";

interface TelegramUserContext {
  telegramId: string | null;
  telegramUsername: string | null;
  telegramDisplayName: string | null;
}

interface PosContextValue {
  cart: ReturnType<typeof usePosCart>;
  screen: PosScreen;
  setScreen: (screen: PosScreen) => void;
  displayMode: PosDisplayMode;
  setDisplayMode: (mode: PosDisplayMode) => void;
  activeCategoryId: string;
  setActiveCategoryId: (id: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedPaymentId: string | null;
  setSelectedPaymentId: (id: string | null) => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  isOrderHistoryOpen: boolean;
  setIsOrderHistoryOpen: (open: boolean) => void;
  selectedProduct: TProductWithRelation | null;
  setSelectedProduct: (product: TProductWithRelation | null) => void;
  isDesktop: boolean;
  createdOrder: OrderResponse | null;
  isCheckoutLoading: boolean;
  orderHistory: PosOrderHistoryItem[];
  telegramId: string | null;
  telegramUsername: string | null;
  telegramDisplayName: string | null;
  customerByTelegram: Customer | null;
  isCustomerByTelegramLoading: boolean;
  handleViewCart: () => void;
  handleBackToMenu: () => void;
  handleCheckout: (options?: { customerId?: string }) => Promise<void>;
  handlePaymentConfirm: () => void;
  handleToggleCart: () => void;
  handleToggleOrderHistory: () => void;
  handleOpenProductDetail: (product: TProductWithRelation) => void;
  handleAddProductFromModal: () => void;
}

const PosContext = createContext<PosContextValue | undefined>(undefined);

function resolveInitialDisplayMode(): PosDisplayMode {
  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get("view")?.trim().toLowerCase();
  if (viewParam === "variants" || viewParam === "variant") {
    return "variants";
  }
  if (viewParam === "products" || viewParam === "product") {
    return "products";
  }
  const stored = getStorage(POS_DISPLAY_MODE_KEY);
  if (stored === "variants" || stored === "products") {
    return stored;
  }
  return "products";
}

function resolveTelegramUserContext(): TelegramUserContext {
  const tgWebApp = (window as any)?.Telegram?.WebApp;
  const tgUser = tgWebApp?.initDataUnsafe?.user;

  if (tgUser && tgUser.id) {
    const fullName = [tgUser.first_name, tgUser.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return {
      telegramId: String(tgUser.id),
      telegramUsername: tgUser.username ? String(tgUser.username) : null,
      telegramDisplayName: fullName || (tgUser.username ? String(tgUser.username) : null),
    };
  }

  const params = new URLSearchParams(window.location.search);
  const telegramId = params.get("telegram_id")?.trim() || null;
  const telegramUsername = params.get("telegram_username")?.trim() || null;
  const firstName = params.get("first_name")?.trim() || "";
  const lastName = params.get("last_name")?.trim() || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return {
    telegramId,
    telegramUsername,
    telegramDisplayName: fullName || telegramUsername || null,
  };
}

export function PosProvider({ children }: { children: ReactNode }) {
  const cart = usePosCart();
  const [, setLocation] = useLocation();
  const [displayMode, setDisplayModeState] = useState<PosDisplayMode>(
    resolveInitialDisplayMode
  );
  const setDisplayMode = useCallback((mode: PosDisplayMode) => {
    setDisplayModeState(mode);
    setStorage(POS_DISPLAY_MODE_KEY, mode);
  }, []);
  const [telegramUser, setTelegramUser] = useState<TelegramUserContext>(
    resolveTelegramUserContext
  );

  useEffect(() => {
    const tgWebApp = (window as any)?.Telegram?.WebApp;
    if (tgWebApp) {
      try {
        tgWebApp.ready?.();
        tgWebApp.expand?.();
      } catch {
        // Ignore Telegram SDK init errors outside Telegram WebView
      }
      const resolved = resolveTelegramUserContext();
      if (resolved.telegramId && resolved.telegramId !== telegramUser.telegramId) {
        setTelegramUser(resolved);
      }
    }
  }, [telegramUser.telegramId]);

  const telegramId = telegramUser.telegramId;
  const telegramUsername = telegramUser.telegramUsername;
  const telegramDisplayName = telegramUser.telegramDisplayName;

  const {
    data: customerByTelegram,
    isLoading: isCustomerByTelegramLoading,
  } = useCollectionData<Customer | null>(
    telegramId ? `/pos/customers/by-telegram/${telegramId}` : null,
    telegramId
      ? () => getPosCustomerByTelegram(telegramId)
      : () => Promise.resolve({ data: null } as any),
    {
      revalidateOnFocus: false,
    }
  );

  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [screen, setScreen] = useState<PosScreen>("menu");
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<TProductWithRelation | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<OrderResponse | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState<PosOrderHistoryItem[]>(() => {
    const stored = getStorage(POS_ORDER_HISTORY_KEY);
    return Array.isArray(stored) ? stored : [];
  });

  const appendOrderToHistory = useCallback((order: OrderResponse) => {
    const item: PosOrderHistoryItem = {
      id: String(order.id),
      orderNumber: order.orderNumber ?? "",
      totalAmount: order.totalAmount ?? 0,
      date: order.createdAt ?? new Date().toISOString(),
    };
    setOrderHistory((prev) => {
      const next = [item, ...prev].slice(0, MAX_ORDER_HISTORY);
      setStorage(POS_ORDER_HISTORY_KEY, next);
      return next;
    });
  }, []);

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  const handleViewCart = useCallback(() => {
    if (isDesktop) {
      setIsCartOpen(true);
    } else {
      setScreen("cart");
    }
  }, [isDesktop]);

  const handleBackToMenu = useCallback(() => {
    setScreen("menu");
    setSearchTerm("");
  }, []);

  const handleCheckout = useCallback(
    async (options?: { customerId?: string }) => {
      if (cart.items.length === 0) return;

      const customerInfo = getStorage(POS_CUSTOMER_INFO_KEY) as
        | { customerName?: string; customerPhone?: string; customerAddress?: string }
        | null;
      const customerName = customerInfo?.customerName?.trim() ?? "";
      const customerPhone = customerInfo?.customerPhone?.trim() ?? "";
      const customerAddress = customerInfo?.customerAddress?.trim() ?? "";
      if (!customerName || !customerPhone) {
        return;
      }

      setIsCheckoutLoading(true);
      try {
        const response = await createPosOrder(
          cart.items,
          customerName,
          customerPhone,
          options?.customerId,
          customerAddress || undefined,
          {
            source: "telegram",
            telegramId,
            telegramUsername,
          }
        );
        if (response.success && response.data) {
          setCreatedOrder(response.data);
          setIsCartOpen(false);

          // Persist to storage synchronously BEFORE navigation so data survives unmount
          const historyItem: PosOrderHistoryItem = {
            id: String(response.data.id),
            orderNumber: response.data.orderNumber ?? "",
            totalAmount: response.data.totalAmount ?? 0,
            date: response.data.createdAt ?? new Date().toISOString(),
          };
          const storedHistory = getStorage(POS_ORDER_HISTORY_KEY);
          const prevHistory = Array.isArray(storedHistory) ? storedHistory : [];
          const nextHistory = [historyItem, ...prevHistory].slice(0, MAX_ORDER_HISTORY);
          setStorage(POS_ORDER_HISTORY_KEY, nextHistory);
          setStorage(POS_CART_STORAGE_KEY, { items: [] });

          cart.clear();
          appendOrderToHistory(response.data);
          setLocation(routes.miniApp.order(response.data.id));
        } else {
          toast({
            title: "Error",
            description: response.message || "Failed to create order",
            variant: "destructive",
          });
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { message?: string } }).response?.message
            : "Failed to create order";
        toast({
          title: "Error",
          description: String(msg),
          variant: "destructive",
        });
      } finally {
        setIsCheckoutLoading(false);
      }
    },
    [cart.items, cart, appendOrderToHistory, setLocation, telegramId, telegramUsername]
  );

  const handlePaymentConfirm = useCallback(() => {
    if (selectedPaymentId) {
      setScreen("confirmation");
      setCreatedOrder(null);
    }
  }, [selectedPaymentId]);

  const handleToggleCart = useCallback(() => {
    if (isDesktop) {
      setIsCartOpen((prev) => !prev);
    } else {
      setScreen("cart");
    }
  }, [isDesktop]);

  const handleToggleOrderHistory = useCallback(() => {
    setIsOrderHistoryOpen((prev) => !prev);
  }, []);

  const handleOpenProductDetail = useCallback(
    (product: TProductWithRelation) => {
      setSelectedProduct(product);
    },
    []
  );

  const handleAddProductFromModal = useCallback(() => {
    setSelectedProduct((product) => {
      if (product) {
        const v = product.productVariants?.[0];
        const price = v?.price ?? product.product.price;
        const imageUrl = v?.imageUrl ?? product.product.imageUrl;
        const name = v?.name ?? product.product.name;
        cart.addItem({
          productId: product.product.id,
          variantId: v?.id ?? product.product.id,
          name,
          imageUrl,
          price: typeof price === "number" ? price : 0,
        });
      }
      return product;
    });
  }, [cart]);

  const value: PosContextValue = useMemo(
    () => ({
      cart,
      screen,
      setScreen,
      displayMode,
      setDisplayMode,
      activeCategoryId,
      setActiveCategoryId,
      searchTerm,
      setSearchTerm,
      selectedPaymentId,
      setSelectedPaymentId,
      isCartOpen,
      setIsCartOpen,
      isOrderHistoryOpen,
      setIsOrderHistoryOpen,
      selectedProduct,
      setSelectedProduct,
      isDesktop,
      createdOrder,
      isCheckoutLoading,
      orderHistory,
      telegramId,
      telegramUsername,
      telegramDisplayName,
      customerByTelegram: customerByTelegram ?? null,
      isCustomerByTelegramLoading,
      handleViewCart,
      handleBackToMenu,
      handleCheckout,
      handlePaymentConfirm,
      handleToggleCart,
      handleToggleOrderHistory,
      handleOpenProductDetail,
      handleAddProductFromModal,
    }),
    [
      cart,
      screen,
      displayMode,
      setDisplayMode,
      activeCategoryId,
      searchTerm,
      selectedPaymentId,
      isCartOpen,
      isOrderHistoryOpen,
      selectedProduct,
      isDesktop,
      createdOrder,
      isCheckoutLoading,
      orderHistory,
      telegramId,
      telegramUsername,
      telegramDisplayName,
      customerByTelegram,
      isCustomerByTelegramLoading,
      handleViewCart,
      handleBackToMenu,
      handleCheckout,
      handlePaymentConfirm,
      handleToggleCart,
      handleToggleOrderHistory,
      handleOpenProductDetail,
      handleAddProductFromModal,
    ]
  );

  return (
    <PosContext.Provider value={value}>{children}</PosContext.Provider>
  );
}

export function usePosContext() {
  const context = useContext(PosContext);
  if (context === undefined) {
    throw new Error("usePosContext must be used within a PosProvider");
  }
  return context;
}
